/**
 * Renderer creation and backend selection (non-negotiable #5).
 *
 * `WebGPURenderer` targets WebGPU and falls back to WebGL2 on its own when the browser has
 * no adapter. `?forcewebgl=1` pins the fallback so the WebGL2 path can be exercised on a
 * machine that has WebGPU — the fallback must never be allowed to rot.
 *
 * `updateFixed` / `updateFrame`: neither. Resizing is event-driven (ResizeObserver), and the
 * loop calls `render()` directly.
 */

import { ACESFilmicToneMapping, PCFSoftShadowMap } from 'three';
import { WebGPURenderer } from 'three/webgpu';
import type { QualitySettings } from '../core/quality.js';

export type RendererBackend = 'webgpu' | 'webgl2';

export interface RendererBundle {
  renderer: WebGPURenderer;
  /** Which backend actually initialised — shown in the F3 overlay. */
  backend: RendererBackend;
  /** True when the WebGL2 path was forced by `?forcewebgl=1` rather than chosen. */
  forcedFallback: boolean;
}

/** Was the WebGL2 fallback requested via the query string? */
export function isWebGLForced(search: string = globalThis.location?.search ?? ''): boolean {
  const params = new URLSearchParams(search);
  const value = params.get('forcewebgl');
  return value !== null && value !== '0' && value !== 'false';
}

export interface CreateRendererOptions {
  canvas: HTMLCanvasElement;
  settings: QualitySettings;
  forceWebGL?: boolean;
}

/**
 * Build and initialise the renderer. Async only at start-up — nothing here is allowed near
 * the frame path (coding standards: no `await` inside a frame).
 */
export async function createRenderer(options: CreateRendererOptions): Promise<RendererBundle> {
  const forceWebGL = options.forceWebGL ?? isWebGLForced();

  const renderer = new WebGPURenderer({
    canvas: options.canvas,
    antialias: false, // MSAA is a tier decision; see applyQualityToRenderer
    alpha: false,
    forceWebGL,
  });

  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.shadowMap.enabled = options.settings.shadows.enabled;
  renderer.shadowMap.type = PCFSoftShadowMap;

  await renderer.init();

  const backend = (renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend === true
    ? 'webgpu'
    : 'webgl2';

  return { renderer, backend, forcedFallback: forceWebGL };
}

/** Push tier settings that the renderer itself owns. Called on every quality change. */
export function applyQualityToRenderer(renderer: WebGPURenderer, settings: QualitySettings): void {
  renderer.shadowMap.enabled = settings.shadows.enabled;
}

/**
 * Size the drawing buffer to the canvas' CSS box, honouring the tier's pixel-ratio cap and
 * render scale. Returns the resolution actually used [px] so the overlay can display it.
 */
export function resizeRenderer(
  renderer: WebGPURenderer,
  canvas: HTMLCanvasElement,
  settings: QualitySettings,
): { widthPx: number; heightPx: number; pixelRatio: number } {
  const cssWidth = Math.max(1, canvas.clientWidth);
  const cssHeight = Math.max(1, canvas.clientHeight);
  const devicePixelRatio = globalThis.devicePixelRatio ?? 1;
  const pixelRatio = Math.min(devicePixelRatio, settings.maxPixelRatio) * settings.renderScale;

  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(cssWidth, cssHeight, false);

  return {
    widthPx: Math.round(cssWidth * pixelRatio),
    heightPx: Math.round(cssHeight * pixelRatio),
    pixelRatio,
  };
}
